import React, { useState } from 'react';
import axios from 'axios';
import WithdrawalForm from './WithdrawalForm';
import './RealizedGains.css';
import { API_BASE_URL } from '../lib/api';

function WithdrawalsTable({ withdrawals, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [editingWithdrawal, setEditingWithdrawal] = useState(null);

  const handleEdit = (withdrawal) => {
    setEditingWithdrawal(withdrawal);
    setShowForm(true);
  };

  const handleDelete = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this withdrawal?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/transactions/withdrawals/${transactionId}`, {
        withCredentials: true
      });
      onRefresh();
    } catch (err) {
      console.error('Error deleting withdrawal:', err);
      alert('Failed to delete withdrawal');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingWithdrawal(null);
    onRefresh();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingWithdrawal(null);
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Calculate totals
  const totalEUR = withdrawals.reduce((sum, w) => sum + (w.amountEUR || 0), 0);
  const totalUSD = withdrawals.reduce((sum, w) => sum + (w.amountUSD || 0), 0);

  return (
    <div className="withdrawals-section">
      <div className="section-header">
        <button onClick={() => setShowForm(true)} className="btn-add">
          + Add Withdrawal
        </button>
      </div>

      {showForm && (
        <WithdrawalForm
          withdrawal={editingWithdrawal}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}

      {withdrawals.length === 0 ? (
        <div className="empty-state">
          <p>No withdrawals recorded yet.</p>
          <p className="empty-hint">Click "+ Add Withdrawal" to record a withdrawal from your investing accounts.</p>
        </div>
      ) : (
        <>
          <div className="table-wrapper">
            <table className="withdrawals-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Amount (EUR)</th>
                  <th>Amount (USD)</th>
                  <th>Platform/Broker</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map(withdrawal => (
                  <tr key={withdrawal.transactionId}>
                    <td>{formatDate(withdrawal.date)}</td>
                    <td>
                      <strong>{formatCurrency(withdrawal.amount, withdrawal.currency)}</strong>
                    </td>
                    <td>{formatCurrency(withdrawal.amountEUR, 'EUR')}</td>
                    <td>{formatCurrency(withdrawal.amountUSD, 'USD')}</td>
                    <td>{withdrawal.platform || '-'}</td>
                    <td>
                      <div className="notes-cell" title={withdrawal.notes}>
                        {withdrawal.notes || '-'}
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(withdrawal)}
                          className="btn-edit"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(withdrawal.transactionId)}
                          className="btn-delete"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-summary">
            <p>
              <strong>Total Withdrawals:</strong> {withdrawals.length} withdrawal{withdrawals.length !== 1 ? 's' : ''} | {' '}
              {formatCurrency(totalEUR, 'EUR')} | {formatCurrency(totalUSD, 'USD')}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default WithdrawalsTable;
